import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Building2, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { CompanyProfile } from '../types/auth';
import { US_STATES } from '../lib/usStates';

export function CompanyProfileForm() {
  const { companyProfile, updateCompanyProfile } = useAuth();
  const [formData, setFormData] = useState<Partial<CompanyProfile>>({
    company_name: '',
    preparer_name: '',
    preparer_title: '',
    preparer_license: '',
    company_address: '',
    company_city: '',
    company_state: '',
    company_zip: '',
    company_phone: '',
    company_email: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (companyProfile) {
      setFormData({
        company_name: companyProfile.company_name || '',
        preparer_name: companyProfile.preparer_name || '',
        preparer_title: companyProfile.preparer_title || '',
        preparer_license: companyProfile.preparer_license || '',
        company_address: companyProfile.company_address || '',
        company_city: companyProfile.company_city || '',
        company_state: companyProfile.company_state || '',
        company_zip: companyProfile.company_zip || '',
        company_phone: companyProfile.company_phone || '',
        company_email: companyProfile.company_email || '',
      });
    }
  }, [companyProfile]);

  function formatPhone(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  function isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  const handleChange = (field: keyof CompanyProfile, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error: updateError } = await updateCompanyProfile(formData);

    if (updateError) {
      setError(updateError.message);
      setLoading(false);
    } else {
      setSuccess(true);
      setLoading(false);
      setTimeout(() => setSuccess(false), 3000);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Building2 className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Company Profile</h2>
          <p className="text-sm text-gray-600">
            Your company details will be automatically used in project reports
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800">Company profile updated successfully!</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
              data-testid="profile-company-name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Acme Electrical Services"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preparer Name
            </label>
            <input
              type="text"
              value={formData.preparer_name}
              onChange={(e) => handleChange('preparer_name', e.target.value)}
              data-testid="profile-preparer-name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="John Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preparer Title
            </label>
            <input
              type="text"
              value={formData.preparer_title}
              onChange={(e) => handleChange('preparer_title', e.target.value)}
              data-testid="profile-preparer-title"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Licensed Electrician"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              License Number
            </label>
            <input
              type="text"
              value={formData.preparer_license}
              onChange={(e) => handleChange('preparer_license', e.target.value)}
              data-testid="profile-preparer-license"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="VT-12345"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Street Address
            </label>
            <input
              type="text"
              value={formData.company_address}
              onChange={(e) => handleChange('company_address', e.target.value)}
              data-testid="profile-company-address"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="123 Main Street"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City
            </label>
            <input
              type="text"
              value={formData.company_city}
              onChange={(e) => handleChange('company_city', e.target.value)}
              data-testid="profile-company-city"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Burlington"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State
            </label>
            <select
              value={formData.company_state || ''}
              onChange={(e) => handleChange('company_state', e.target.value)}
              data-testid="profile-company-state"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a state</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ZIP Code
            </label>
            <input
              type="text"
              value={formData.company_zip}
              onChange={(e) => handleChange('company_zip', e.target.value)}
              data-testid="profile-company-zip"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="05401"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.company_phone}
              data-testid="profile-company-phone"
              onChange={(e) => {
                const formatted = formatPhone(e.target.value);
                handleChange('company_phone', formatted);
                if (phoneError && formatted.replace(/\D/g, '').length === 10) setPhoneError('');
              }}
              onBlur={() => {
                const digits = (formData.company_phone || '').replace(/\D/g, '');
                if (digits.length > 0 && digits.length !== 10) setPhoneError('Please enter a valid 10-digit phone number.');
                else setPhoneError('');
              }}
              placeholder="(802) 555-1234"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {phoneError && <p className="text-xs text-red-600 mt-1">{phoneError}</p>}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Email
            </label>
            <input
              type="email"
              value={formData.company_email}
              data-testid="profile-company-email"
              onChange={(e) => {
                handleChange('company_email', e.target.value);
                if (emailError && isValidEmail(e.target.value)) setEmailError('');
              }}
              onBlur={() => {
                const val = formData.company_email || '';
                if (val && !isValidEmail(val)) setEmailError('Please enter a valid email address.');
                else setEmailError('');
              }}
              placeholder="contact@acmeelectrical.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {emailError && <p className="text-xs text-red-600 mt-1">{emailError}</p>}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            data-testid="btn-profile-save"
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-5 h-5" />
            {loading ? 'Saving...' : 'Save Company Profile'}
          </button>
        </div>
      </form>
    </div>
  );
}
